import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Printer } from "lucide-react";
import { toast } from "sonner";
import { brl, cleanItemName } from "@/api";

// ─────────────────────────────────────────────────────────────────────────────
// Cupom térmico — abre uma janela/iframe oculto com HTML otimizado para
// impressoras 58mm/80mm. Funciona para Venda/Pedido (`type="sale"`) e para
// Ordem de Serviço (`type="service"`). NÃO substitui o recibo A4 existente.
// O `data` esperado:
//   - sale: { order_number, created_at, items, subtotal, total, discount,
//            payment_method, payments[], trade_in, fees[], note, customer }
//   - os:   { os_number, created_at, product_name, product_info, customer_*,
//            service_value, items[], payment_method, payments[], trade_in,
//            fees[], note, total }
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return ""; }
};

const renderThermalHtml = (data, type, store, width) => {
  // Larguras em mm. CSS @page com largura ajustada → impressora térmica respeita.
  const W = width === 80 ? 80 : 58;
  // CSS minimalista; toda a tipografia é monospace pra ficar alinhada na térmica.
  // ⚠ IMPORTANTE — `@page { margin: 0 }` é o que faz o Chrome/Edge esconder
  // automaticamente cabeçalho/rodapé (URL, data, "Página 1 de 1") na maioria
  // dos casos. A largura/altura do cupom é controlada pelo `size`.
  const css = `
    @page { size: ${W}mm auto; margin: 0; }
    @media print {
      html, body { margin: 0 !important; padding: 0 !important; }
    }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: 'Courier New', 'Courier', monospace; }
    body { width: ${W}mm; font-size: ${W === 80 ? 12 : 10}px; line-height: 1.35; padding: 3mm 2mm 6mm 2mm; }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: bold; }
    .lg { font-size: ${W === 80 ? 15 : 12}px; }
    .xl { font-size: ${W === 80 ? 17 : 14}px; }
    .sm { font-size: ${W === 80 ? 11 : 9}px; }
    hr { border: none; border-top: 1px dashed #000; margin: 4px 0; }
    .row { display: flex; justify-content: space-between; gap: 4px; }
    .item-name { word-break: break-word; }
    .totals .row { padding: 1px 0; }
    .totals .row.total { font-size: ${W === 80 ? 15 : 12}px; font-weight: bold; border-top: 1px solid #000; padding-top: 3px; margin-top: 3px; }
    .badge { display: inline-block; border: 1px dashed #000; padding: 2px 4px; margin: 2px 0; font-size: ${W === 80 ? 11 : 9}px; }
    .logo { max-width: ${W - 10}mm; max-height: 28mm; display: block; margin: 0 auto 2mm; }
    img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; vertical-align: top; padding: 1px 0; }
    th { border-bottom: 1px solid #000; font-size: ${W === 80 ? 11 : 9}px; }
    .qx { text-align: right; padding-left: 4px; }
    .sign-line { border-top: 1px solid #000; margin-top: 22px; padding-top: 2px; }
  `;

  const isSale = type === "sale";
  const orderNum = isSale
    ? String(data.order_number || "?").padStart(6, "0")
    : `OS ${String(data.os_number || "?").padStart(6, "0")}`;
  const items = data.items || [];
  const payments = Array.isArray(data.payments) ? data.payments.filter(p => p && p.method) : [];
  const fees = Array.isArray(data.fees) ? data.fees : [];
  const feesTotal = fees.reduce((s, f) => s + Number(f.amount || 0), 0);
  const ti = data.trade_in;
  const subtotal = isSale ? Number(data.subtotal || 0) : 0;
  const discount = isSale ? Number(data.discount || 0) : 0;
  // Para OS: total = soma items.value + service_value
  const serviceItemsTotal = !isSale
    ? items.reduce((s, it) => s + Number(it.value || 0), 0)
    : 0;
  const serviceValue = !isSale ? Number(data.service_value || 0) : 0;
  const total = isSale ? Number(data.total || 0) : (serviceItemsTotal + serviceValue);
  const grandTotal = total + feesTotal;
  const customer = data.customer || {
    name: data.customer_name, phone: data.customer_phone,
    cpf: data.customer_cpf, address: data.customer_address,
  };

  const logoHtml = (store?.receipt_show_logo !== false && store?.logo)
    ? `<img src="${store.logo}" class="logo" crossorigin="anonymous"/>` : "";

  const headHtml = `
    ${logoHtml}
    <div class="center bold lg">${(store?.name || "Loja").replace(/</g, "")}</div>
    ${store?.receipt_show_address !== false && store?.address ? `<div class="center sm">${store.address}</div>` : ""}
    ${store?.receipt_show_phone !== false && store?.phone ? `<div class="center sm">${store.phone}</div>` : ""}
    ${store?.receipt_show_cnpj !== false && store?.cnpj ? `<div class="center sm">CNPJ: ${store.cnpj}</div>` : ""}
    <hr/>
    <div class="row sm"><span>${isSale ? "PEDIDO" : "ORDEM SERV."} #${orderNum}</span><span>${fmtDate(data.created_at)}</span></div>
  `;

  const customerHtml = (customer?.name || customer?.phone || customer?.cpf) ? `
    <hr/>
    <div class="bold sm">CLIENTE</div>
    ${customer.name ? `<div>${customer.name}</div>` : ""}
    ${customer.cpf ? `<div class="sm">CPF: ${customer.cpf}</div>` : ""}
    ${customer.phone ? `<div class="sm">${customer.phone}</div>` : ""}
    ${customer.address ? `<div class="sm">${customer.address}</div>` : ""}
  ` : "";

  // Itens
  let itemsHtml = "";
  if (isSale) {
    itemsHtml = `<hr/><table><thead><tr><th>Item</th><th class="qx">Qtd</th><th class="qx">Sub</th></tr></thead><tbody>`;
    for (const it of items) {
      const name = cleanItemName(it.name || "");
      const sub = Number(it.unit_price || 0) * Number(it.quantity || 0);
      const disc = Math.max(0, Number(it.item_discount || 0));
      const final = Math.max(0, sub - disc);
      itemsHtml += `<tr>
        <td class="item-name">${name}${it.variant_name ? `<div class="sm">(${it.variant_name})</div>` : ""}</td>
        <td class="qx">${it.quantity}</td>
        <td class="qx">${disc > 0 ? `<s class="sm">${brl(sub)}</s><br/>${brl(final)}` : brl(sub)}</td>
      </tr>`;
    }
    itemsHtml += `</tbody></table>`;
  } else {
    // OS: produto recebido + items de peças + serviço
    itemsHtml = `<hr/>
      <div class="bold sm">PRODUTO RECEBIDO</div>
      <div class="bold">${(data.product_name || "—").replace(/</g, "")}</div>
      ${data.product_info ? `<div class="sm">${data.product_info}</div>` : ""}
      ${data.product_details ? `<div class="sm">${data.product_details}</div>` : ""}
    `;
    if (items.length > 0) {
      itemsHtml += `<hr/><div class="bold sm">PECAS / SERVICOS</div>`;
      for (const it of items) {
        itemsHtml += `<div class="row"><span class="item-name">${(it.description || "").replace(/</g, "")}</span><span>${brl(it.value)}</span></div>`;
      }
    }
    if (serviceValue > 0) {
      itemsHtml += `<div class="row"><span>Mao de obra</span><span>${brl(serviceValue)}</span></div>`;
    }
  }

  // Trade-in
  const tiHtml = ti ? `
    <hr/>
    <div class="badge bold">PRODUTO RECEBIDO EM TROCA</div>
    <div>${(ti.product_name || "").replace(/</g, "")}${ti.brand ? ` - ${ti.brand}` : ""}</div>
    ${ti.code ? `<div class="sm">Cod: ${ti.code}</div>` : ""}
    ${ti.condition ? `<div class="sm">Estado: ${ti.condition}</div>` : ""}
    <div class="row"><span>Valor abatido</span><span>-${brl(ti.value)}</span></div>
  ` : "";

  // Totais
  let totalsHtml = `<hr/><div class="totals">`;
  if (isSale) {
    if (subtotal !== total) totalsHtml += `<div class="row"><span>Subtotal</span><span>${brl(subtotal)}</span></div>`;
    if (discount > 0) totalsHtml += `<div class="row"><span>Desconto</span><span>-${brl(discount)}</span></div>`;
  }
  totalsHtml += `<div class="row total"><span>Total</span><span>${brl(total)}</span></div>`;
  if (fees.length > 0) {
    totalsHtml += `<div class="bold sm" style="margin-top:4px">TAXAS EXTRAS</div>`;
    for (const f of fees) {
      totalsHtml += `<div class="row sm"><span>${(f.label || "").replace(/</g, "")}</span><span>+${brl(f.amount)}</span></div>`;
    }
    totalsHtml += `<div class="row total"><span>Total final</span><span>${brl(grandTotal)}</span></div>`;
  }
  // Forma de pagamento
  if (payments.length > 1) {
    totalsHtml += `<div class="bold sm" style="margin-top:4px">PAGAMENTO</div>`;
    for (const p of payments) {
      totalsHtml += `<div class="row sm"><span>${(p.method || "").replace(/</g, "")}${p.note ? ` (${p.note})` : ""}</span><span>${brl(p.amount)}</span></div>`;
    }
  } else {
    totalsHtml += `<div class="row sm"><span>Pagamento</span><span>${(data.payment_method || "—").replace(/</g, "")}</span></div>`;
  }
  totalsHtml += `</div>`;

  // Observação
  const noteHtml = (data.show_note_on_receipt && data.note)
    ? `<hr/><div class="sm bold">OBSERVACAO</div><div class="sm">${(data.note || "").replace(/</g, "")}</div>`
    : "";

  // Termos (se houver na loja e for venda/OS curto)
  const termsText = store?.receipt_terms || "";
  const termsHtml = (store?.receipt_show_terms !== false && termsText)
    ? `<hr/><div class="sm bold">TERMOS</div><div class="sm" style="white-space:pre-line">${(termsText || "").replace(/</g, "")}</div>`
    : "";

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Cupom ${orderNum}</title>
    <style>${css}</style></head>
    <body>${headHtml}${customerHtml}${itemsHtml}${tiHtml}${totalsHtml}${noteHtml}${termsHtml}
      <div class="center sm" style="margin-top:6px">Obrigado!</div>
      <div class="sign-line center sm">Assinatura</div>
      <div style="height:24px"></div>
    </body></html>`;
};

export default function ThermalReceipt({ data, type = "sale", store = {}, onClose }) {
  const [width, setWidth] = useState(58); // 58mm padrão
  const [printing, setPrinting] = useState(false);

  // ── Impressão usando popup window ─────────────────────────────────────
  // Por que NÃO usamos iframe oculto: no Chrome/Edge o iframe com `@page`
  // de tamanho customizado às vezes imprime uma página em branco no 80mm
  // (problema de foco e/ou da pilha de paint quando o body é menor que a
  // janela). O popup resolve, e ainda permite que o browser respeite o
  // `@page { margin: 0 }` — que é o que esconde URL/data/rodapé do navegador
  // na maioria das instalações (Chrome ≥ 89, Edge, Firefox).
  const printNow = () => {
    const html = renderThermalHtml(data, type, store, width);
    // Tamanho da popup proporcional ao W: térmicas precisam de pouca largura.
    const popupW = width === 80 ? 400 : 300;
    const popupH = 700;
    const features = `width=${popupW},height=${popupH},menubar=no,toolbar=no,location=no,status=no,scrollbars=yes`;
    const w = window.open("", "GestaoExpertThermalPrint", features);
    if (!w) {
      toast.error("Permita pop-ups para imprimir o cupom (clique no ícone bloqueado na barra do navegador)");
      return;
    }
    setPrinting(true);
    w.document.open();
    w.document.write(html);
    w.document.close();
    const triggerPrint = () => {
      try { w.focus(); w.print(); } catch { /* ignore */ }
      // Tenta fechar logo após — alguns browsers só liberam após o usuário
      // confirmar/cancelar o dialog de impressão.
      setTimeout(() => { try { w.close(); } catch {} setPrinting(false); }, 700);
    };
    // Aguarda imagens (logo) terminarem de carregar pra não imprimir sem logo
    const doc = w.document;
    const imgs = doc.querySelectorAll("img");
    if (imgs.length === 0) {
      setTimeout(triggerPrint, 250);
    } else {
      let pending = imgs.length;
      let fired = false;
      const tick = () => {
        pending--;
        if (pending <= 0 && !fired) { fired = true; setTimeout(triggerPrint, 200); }
      };
      imgs.forEach(img => { if (img.complete) tick(); else { img.onload = tick; img.onerror = tick; } });
      // Fallback de segurança caso alguma imagem trave
      setTimeout(() => { if (!fired) { fired = true; triggerPrint(); } }, 3000);
    }
  };

  // Preview no modal — mostra exatamente o mesmo HTML que será impresso.
  const previewHtml = renderThermalHtml(data, type, store, width);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 no-print" data-testid="thermal-receipt-modal">
      <div className="bg-white max-w-md w-full max-h-[92vh] rounded-md flex flex-col overflow-hidden">
        <div className="p-3 border-b border-[#E5E5E5] flex items-center justify-between gap-2">
          <div className="text-sm font-display font-medium">Cupom térmico</div>
          <div className="flex gap-1">
            <button
              onClick={() => setWidth(58)}
              className={`text-[11px] px-2 py-1 rounded border ${width === 58 ? "bg-black text-white border-black" : "border-[#E5E5E5]"}`}
              data-testid="thermal-width-58"
            >58 mm</button>
            <button
              onClick={() => setWidth(80)}
              className={`text-[11px] px-2 py-1 rounded border ${width === 80 ? "bg-black text-white border-black" : "border-[#E5E5E5]"}`}
              data-testid="thermal-width-80"
            >80 mm</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-neutral-100 p-3 flex justify-center">
          <iframe
            title="Preview cupom"
            srcDoc={previewHtml}
            style={{ width: width === 80 ? "300px" : "220px", height: "60vh", background: "#fff", border: "1px solid #ddd" }}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 p-3 border-t border-[#E5E5E5]">
          <Button variant="outline" onClick={onClose} data-testid="thermal-receipt-close"><X size={14} className="mr-1"/> Fechar</Button>
          <Button onClick={printNow} disabled={printing} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="thermal-receipt-print">
            <Printer size={14} className="mr-1"/> {printing ? "Imprimindo…" : "Imprimir cupom"}
          </Button>
        </div>
      </div>
    </div>
  );
}
